import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PortalLayout from '../PortalLayout';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import { reportingService, dashboardService } from '../../../services/api';
import FinancialPerformancePage from './FinancialPerformancePage';
import ProductIntelligencePage from './ProductIntelligencePage';
import CostManagementPage from './CostManagementPage';
import OperationalEfficiencyPage from './OperationalEfficiencyPage';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileCard from '../../../components/common/MobileCard';
import MobileHero from '../../../components/common/MobileHero';
import { C } from '../../../styles/theme';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell,
} from 'recharts';

/* ── Brand tokens ─────────────────────────────────────────── */
const FONT = "'Plus Jakarta Sans', sans-serif";

const PIE_COLORS = [C.green, C.muted, C.mutedLight, '#0891b2', '#d97706', '#7c3aed'];
const PAYMENT_BADGES = {
    Cash:            { bg: '#eff5ff', color: '#2563eb' },
    Card:            { bg: '#eff6ff', color: '#2563eb' },
    'Split Payment': { bg: '#fef3c7', color: '#d97706' },
};
const EXTRA_BADGE_COLORS = [
    { bg: '#f5f3ff', color: '#7c3aed' },  // purple
    { bg: '#fdf2f8', color: '#db2777' },  // pink
    { bg: '#ecfeff', color: '#0891b2' },  // teal
    { bg: '#fff7ed', color: '#ea580c' },  // orange
    { bg: '#fefce8', color: '#ca8a04' },  // yellow
    { bg: '#eff5ff', color: '#16a34a' },  // lime
];
const _dynamicBadgeMap = {};
const getPaymentBadge = (method) => {
    if (PAYMENT_BADGES[method]) return PAYMENT_BADGES[method];
    if (!_dynamicBadgeMap[method]) {
        const usedCount = Object.keys(_dynamicBadgeMap).length;
        _dynamicBadgeMap[method] = EXTRA_BADGE_COLORS[usedCount % EXTRA_BADGE_COLORS.length];
    }
    return _dynamicBadgeMap[method];
};

/* ── Helpers ──────────────────────────────────────────────── */
const fmt = (v, currency) => {
    if (v == null) return currency + '0';
    return currency + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const pctChange = (curr, prev) => {
    if (!prev || prev === 0) return curr > 0 ? 100 : 0;
    return ((curr - prev) / Math.abs(prev)) * 100;
};

const toISO = (d) => d.toISOString().split('T')[0];

const getToday = () => toISO(new Date());

const getStartOfWeek = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return toISO(d);
};

const getEndOfWeek = () => {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? 0 : 7 - day));
    return toISO(d);
};

const getStartOfMonth = () => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-01';
};

/* ── SVG Icons ────────────────────────────────────────────── */
const ChartIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
);

const DownloadIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
);

/* ── Shared sub-components ────────────────────────────────── */

const EmptyState = ({ icon, title, subtitle }) => (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ marginBottom: 16, opacity: 0.7 }}>{icon}</div>
        <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>{title}</div>
        <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted }}>{subtitle}</div>
    </div>
);

const KpiCard = ({ label, value, change, invertColor, t }) => {
    const positive = invertColor ? change <= 0 : change >= 0;
    const changeColor = positive ? C.green : C.red;
    const arrow = change >= 0 ? '\u2191' : '\u2193';
    return (
        <div style={{
            background: C.white, border: '1.5px solid ' + C.border, borderRadius: 12,
            padding: '20px 22px', flex: 1, minWidth: 0,
        }}>
            <div style={{
                fontFamily: FONT, fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 8,
            }}>{label}</div>
            <div style={{ fontFamily: FONT, fontSize: 28, fontWeight: 800, color: C.text, marginBottom: 6 }}>{value}</div>
            {change != null && (
                <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: changeColor }}>
                    {arrow} {Math.abs(change).toFixed(1)}% {t('vs_prev_period') || 'vs prev period'}
                </div>
            )}
        </div>
    );
};

/* ── Date Range Picker ────────────────────────────────────── */

const getPresets = (t) => [
    { key: 'today', label: t('today') || 'Today' },
    { key: 'week',  label: t('this_week') || 'This Week' },
    { key: 'month', label: t('this_month') || 'This Month' },
    { key: 'custom', label: t('custom') || 'Custom' },
];

const DateRangePicker = ({ preset, setPreset, startDate, setStartDate, endDate, setEndDate, t, isMobile }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
        <div style={{
            display: 'flex', background: C.bg, borderRadius: 9,
            border: '1.5px solid ' + C.border, overflow: 'hidden',
            width: isMobile ? '100%' : 'auto',
        }}>
            {getPresets(t).map((p) => (
                <button
                    key={p.key}
                    onClick={() => setPreset(p.key)}
                    style={{
                        fontFamily: FONT, fontSize: isMobile ? 13 : 12, fontWeight: 600,
                        padding: isMobile ? '9px 6px' : '7px 14px', border: 'none', cursor: 'pointer',
                        flex: isMobile ? 1 : 'initial',
                        background: preset === p.key ? C.green : 'transparent',
                        color: preset === p.key ? C.white : C.muted,
                        transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}
                >{p.label}</button>
            ))}
        </div>
        {preset === 'custom' && (
            <React.Fragment>
                <input
                    type="date" value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{
                        fontFamily: FONT, fontSize: 12, padding: '6px 10px',
                        border: '1.5px solid ' + C.border, borderRadius: 8, outline: 'none',
                        flex: isMobile ? 1 : 'initial', minWidth: 0,
                    }}
                />
                <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>{t('to') || 'to'}</span>
                <input
                    type="date" value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{
                        fontFamily: FONT, fontSize: 12, padding: '6px 10px',
                        border: '1.5px solid ' + C.border, borderRadius: 8, outline: 'none',
                        flex: isMobile ? 1 : 'initial', minWidth: 0,
                    }}
                />
            </React.Fragment>
        )}
    </div>
);

/* ── Overview Section ─────────────────────────────────────── */

const OverviewSection = ({ data, loading, currency, t }) => {
    const isMobile = useIsMobile();
    if (loading) {
        return <div style={{ fontFamily: FONT, fontSize: 14, color: C.muted, padding: '60px 0', textAlign: 'center' }}>{t('loading') || 'Loading...'}</div>;
    }
    if (!data || !data.kpis) {
        return <EmptyState icon={<ChartIcon />} title={t('no_data_available') || 'No data available'} subtitle={t('try_different_date_range') || 'Try selecting a different date range'} />;
    }

    const { kpis, revenue_trend, expenses_breakdown, top_products } = data;
    const revChange = pctChange(kpis.revenue, kpis.prev_revenue);
    const ordChange = pctChange(kpis.orders, kpis.prev_orders);
    const avgChange = pctChange(kpis.avg_order_value, kpis.prev_avg_order_value);
    const expChange = pctChange(kpis.expenses, kpis.prev_expenses);

    const isHourly = revenue_trend && revenue_trend.length > 0 && revenue_trend[0].hour != null;

    return (
        <div>
            {/* KPI Cards */}
            {isMobile ? (
                <MobileHero
                    label={t('revenue') || 'Revenue'}
                    value={fmt(kpis.revenue, currency)}
                    meta={`${revChange >= 0 ? '↑' : '↓'} ${Math.abs(revChange).toFixed(1)}% ${t('vs_prev_period') || 'vs prev period'}`}
                    stats={[
                        { label: t('orders') || 'Orders', value: String(kpis.orders), tone: ordChange >= 0 ? 'green' : 'red' },
                        { label: t('avg_order_value') || 'Avg Order Value', value: fmt(kpis.avg_order_value, currency) },
                        { label: t('expenses') || 'Expenses', value: fmt(kpis.expenses, currency), tone: expChange <= 0 ? 'green' : 'red' },
                    ]}
                />
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
                    <KpiCard label={t('revenue') || 'Revenue'} value={fmt(kpis.revenue, currency)} change={revChange} t={t} />
                    <KpiCard label={t('orders') || 'Orders'} value={kpis.orders} change={ordChange} t={t} />
                    <KpiCard label={t('avg_order_value') || 'Avg Order Value'} value={fmt(kpis.avg_order_value, currency)} change={avgChange} t={t} />
                    <KpiCard label={t('expenses') || 'Expenses'} value={fmt(kpis.expenses, currency)} change={expChange} invertColor t={t} />
                </div>
            )}

            {/* Charts Row */}
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>
                {/* Revenue Trend */}
                <div style={{ background: C.white, border: '1.5px solid ' + C.border, borderRadius: 12, padding: 20 }}>
                    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 16 }}>
                        {t('revenue_trend') || 'Revenue Trend'}
                    </div>
                    {revenue_trend && revenue_trend.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={revenue_trend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                <XAxis
                                    dataKey={isHourly ? 'hour' : 'date'}
                                    tickFormatter={(v) => {
                                        if (isHourly) return String(v).padStart(2, '0') + ':00';
                                        if (typeof v === 'string' && v.length >= 10) return v.slice(5);
                                        return String(v);
                                    }}
                                    tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }}
                                    axisLine={false} tickLine={false}
                                />
                                <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                    formatter={(v) => [fmt(v, currency), t('revenue') || 'Revenue']}
                                />
                                <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                                    {revenue_trend.map((entry, i) => (
                                        <Cell key={'rc-' + i} fill={i === revenue_trend.length - 1 ? C.green : C.greenBorder} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState icon={<ChartIcon />} title={t('no_revenue_data') || 'No revenue data for this period'} subtitle="" />
                    )}
                </div>

                {/* Expenses Donut */}
                <div style={{ background: C.white, border: '1.5px solid ' + C.border, borderRadius: 12, padding: 20 }}>
                    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 16 }}>
                        {t('expenses_breakdown') || 'Expenses Breakdown'}
                    </div>
                    {expenses_breakdown && expenses_breakdown.length > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                            <ResponsiveContainer width="50%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={expenses_breakdown}
                                        dataKey="total"
                                        nameKey="category"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                    >
                                        {expenses_breakdown.map((entry, i) => (
                                            <Cell key={'pc-' + i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                        formatter={(v) => [fmt(v, currency), t('amount') || 'Amount']}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ flex: 1 }}>
                                {expenses_breakdown.map((item, i) => (
                                    <div key={item.category} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: 3, background: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: C.text }}>{item.category}</div>
                                            <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>{fmt(item.total, currency)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <EmptyState icon={<ChartIcon />} title={t('no_expense_data') || 'No expense data for this period'} subtitle="" />
                    )}
                </div>
            </div>

            {/* Top Products Table */}
            {top_products && top_products.length > 0 && (
                <div style={{ background: C.white, border: '1.5px solid ' + C.border, borderRadius: 12, padding: 20 }}>
                    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 16 }}>
                        {t('top_products') || 'Top Products'}
                    </div>
                    {isMobile ? (
                        <div>
                            {top_products.map((p, i) => (
                                <MobileCard
                                    key={p.name}
                                    accent={i === 0 ? 'green' : undefined}
                                    title={p.name}
                                    amount={fmt(p.revenue, currency)}
                                    subtitle={`${p.quantity} ${t('qty') || 'Qty'}`}
                                    collapsible={false}
                                />
                            ))}
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 80px 100px', gap: 0 }}>
                            {/* Header */}
                            {['#', t('product') || 'Product', t('qty') || 'Qty', t('revenue') || 'Revenue'].map((h) => (
                                <div key={h} style={{
                                    fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: 0.8, color: C.muted, padding: '8px 12px',
                                    borderBottom: '1.5px solid ' + C.border,
                                }}>{h}</div>
                            ))}
                            {/* Rows */}
                            {top_products.map((p, i) => (
                                <React.Fragment key={p.name}>
                                    <div style={{ padding: '10px 12px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center' }}>
                                        <span style={{
                                            fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                            background: i === 0 ? C.greenLight : C.bg,
                                            color: i === 0 ? C.green : C.muted,
                                            width: 24, height: 24, borderRadius: 6,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        }}>{i + 1}</span>
                                    </div>
                                    <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text, padding: '10px 12px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center' }}>{p.name}</div>
                                    <div style={{ fontFamily: FONT, fontSize: 13, color: C.text, padding: '10px 12px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center' }}>{p.quantity}</div>
                                    <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text, padding: '10px 12px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center' }}>{fmt(p.revenue, currency)}</div>
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


const exportPDF = (overviewRef) => {
    if (!overviewRef?.current) return;
    const content = overviewRef.current;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
        <!DOCTYPE html>
        <html><head>
            <title>Reporting Overview</title>
            <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: 'Plus Jakarta Sans', sans-serif; background: #fff; padding: 24px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                @media print { body { padding: 12px; } }
            </style>
        </head><body>
            ${content.innerHTML}
        </body></html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
    };
};

/* ── Main Page Component ──────────────────────────────────── */

const getNavItems = (t) => [
    { key: 'overview',     label: t('overview') || 'Overview' },
];

const getV2NavItems = (t) => [
    { key: 'financial',   label: t('financial_performance') !== 'financial_performance' ? t('financial_performance') : 'Financial Performance' },
    { key: 'products',    label: t('product_intelligence') !== 'product_intelligence' ? t('product_intelligence') : 'Product Intelligence' },
    { key: 'costs',       label: t('cost_management') !== 'cost_management' ? t('cost_management') : 'Cost Management' },
    // 'operations' (Operational Efficiency) tab hidden — coming soon, stub page kept at ./OperationalEfficiencyPage
];

const PortalReportingPage = () => {
    const { getCurrency } = useAuth();
    const { t } = useLanguage();
    const isMobile = useIsMobile();
    const currency = getCurrency ? getCurrency() : '';

    const overviewRef = useRef(null);
    const [activeSection, setActiveSection] = useState('overview');
    const [preset, setPreset] = useState('today');
    const [customStart, setCustomStart] = useState(getToday);
    const [customEnd, setCustomEnd] = useState(getToday);
    const [loading, setLoading] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(() => localStorage.getItem('etl-last-refresh') || null);

    // Mobile section picker (dropdown) open state
    const [sectionMenuOpen, setSectionMenuOpen] = useState(false);

    const handleRefreshETL = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await dashboardService.refreshAnalytics();
            const now = new Date();
            const ts = `${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
            localStorage.setItem('etl-last-refresh', ts);
            setLastRefresh(ts);
        } catch { /* silent */ }
        setTimeout(() => setRefreshing(false), 30000);
    };

    // Compute effective date range
    const dateRange = useMemo(() => {
        switch (preset) {
            case 'today': return { start: getToday(), end: getToday() };
            case 'week':  return { start: getStartOfWeek(), end: getEndOfWeek() };
            case 'month': return { start: getStartOfMonth(), end: getToday() };
            case 'custom': return { start: customStart, end: customEnd };
            default: return { start: getToday(), end: getToday() };
        }
    }, [preset, customStart, customEnd]);

    // Fetch data
    const fetchData = useCallback(() => {
        setLoading(true);
        reportingService.getPortalSummary(dateRange.start, dateRange.end)
            .then((data) => { setSummaryData(data); })
            .catch((err) => { console.error('Reporting fetch error:', err); setSummaryData(null); })
            .finally(() => { setLoading(false); });
    }, [dateRange.start, dateRange.end]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePresetChange = useCallback((key) => {
        setPreset(key);
    }, []);

    const allTabs = [...getNavItems(t), ...getV2NavItems(t)];

    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT }}>

                {/* Page header + date picker */}
                {isMobile ? (
                    <div style={{ marginBottom: 18 }}>
                        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>{t('analytics') || 'Analytics'}</h2>
                        <div style={{ fontSize: 13, color: C.muted, marginTop: 3, marginBottom: 14 }}>
                            {t('business_performance_glance') || 'Deep-dive into your business performance'}
                        </div>
                        <DateRangePicker
                            preset={preset} setPreset={handlePresetChange}
                            startDate={customStart} setStartDate={setCustomStart}
                            endDate={customEnd} setEndDate={setCustomEnd}
                            t={t} isMobile
                        />
                        {preset !== 'custom' && (
                            <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 8, fontWeight: 600 }}>
                                {new Date(dateRange.start + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                                {dateRange.start !== dateRange.end && ` – ${new Date(dateRange.end + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}`}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                            <button
                                onClick={handleRefreshETL}
                                disabled={refreshing}
                                style={{
                                    flex: 1, fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                    padding: '11px 12px', border: '1.5px solid ' + C.border, borderRadius: 10,
                                    background: C.white, color: refreshing ? C.mutedLight : C.green,
                                    cursor: refreshing ? 'default' : 'pointer',
                                }}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>
                                    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                </svg>
                                {refreshing ? (t('refreshing') || 'Refreshing...') : (t('refresh') || 'Refresh')}
                            </button>
                            {activeSection === 'overview' && (
                                <button
                                    onClick={() => exportPDF(overviewRef)}
                                    style={{
                                        flex: 1, fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                        padding: '11px 12px', border: '1.5px solid ' + C.border, borderRadius: 10,
                                        background: C.white, color: C.text, cursor: 'pointer',
                                    }}
                                >
                                    <DownloadIcon /> {t('export_pdf') || 'Export PDF'}
                                </button>
                            )}
                        </div>
                        {lastRefresh && (
                            <div style={{ fontSize: 10, color: C.mutedLight, marginTop: 8 }}>
                                {t('last_refresh') || 'Last refresh'}: {lastRefresh}
                            </div>
                        )}
                    </div>
                ) : (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>
                            {t('analytics') || 'Analytics'}
                        </h2>
                        <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
                            {t('business_performance_glance') || 'Deep-dive into your business performance'}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                            <DateRangePicker
                                preset={preset}
                                setPreset={handlePresetChange}
                                startDate={customStart}
                                setStartDate={setCustomStart}
                                endDate={customEnd}
                                setEndDate={setCustomEnd}
                                t={t}
                            />
                            {preset !== 'custom' && (
                                <span style={{ fontSize: 10, color: C.mutedLight, whiteSpace: 'nowrap' }}>
                                    {new Date(dateRange.start + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                                    {dateRange.start !== dateRange.end && ` – ${new Date(dateRange.end + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}`}
                                </span>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button
                                onClick={handleRefreshETL}
                                disabled={refreshing}
                                title={t('refresh_analytics') || 'Refresh analytics data'}
                                style={{
                                    fontFamily: FONT, fontSize: 12, fontWeight: 600,
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '7px 12px', border: '1.5px solid ' + C.border, borderRadius: 8,
                                    background: C.white, color: refreshing ? C.mutedLight : C.green,
                                    cursor: refreshing ? 'default' : 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>
                                    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                                </svg>
                                {refreshing ? (t('refreshing') || 'Refreshing...') : (t('refresh') || 'Refresh')}
                            </button>
                            {lastRefresh && (
                                <span style={{ fontSize: 10, color: C.mutedLight }}>
                                    {t('last_refresh') || 'Last refresh'}: {lastRefresh}
                                </span>
                            )}
                        </div>
                        {activeSection === 'overview' && (
                            <button
                                onClick={() => exportPDF(overviewRef)}
                                style={{
                                    fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '8px 16px', border: '1.5px solid ' + C.border, borderRadius: 8,
                                    background: C.white, color: C.text, cursor: 'pointer',
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = C.white; }}
                            >
                                <DownloadIcon /> {t('export_pdf') || 'Export PDF'}
                            </button>
                        )}
                    </div>
                </div>
                )}

                {/* Section navigation — mobile: dropdown picker; desktop: underline tabs */}
                {isMobile ? (
                    <div style={{ position: 'relative', marginBottom: 22, zIndex: 5 }}>
                        <button
                            onClick={() => setSectionMenuOpen(o => !o)}
                            style={{
                                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '13px 16px', borderRadius: 12, border: `1.5px solid ${C.border}`,
                                background: C.white, fontFamily: FONT, cursor: 'pointer',
                            }}
                        >
                            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                                <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.mutedLight }}>{t('section') || 'Section'}</span>
                                <span style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{(allTabs.find(tt => tt.key === activeSection) || allTabs[0] || {}).label}</span>
                            </span>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: sectionMenuOpen ? 'rotate(180deg)' : 'none' }}><polyline points="6 9 12 15 18 9" /></svg>
                        </button>
                        {sectionMenuOpen && (
                            <>
                                <div onClick={() => setSectionMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 41, background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: '0 12px 28px rgba(16,24,40,.14)', overflow: 'hidden' }}>
                                    {allTabs.map((tab) => {
                                        const isActive = activeSection === tab.key;
                                        return (
                                            <button
                                                key={tab.key}
                                                onClick={() => { setActiveSection(tab.key); setSectionMenuOpen(false); }}
                                                style={{
                                                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    padding: '13px 16px', border: 'none', cursor: 'pointer', fontFamily: FONT,
                                                    background: isActive ? C.greenLight : C.white,
                                                    color: isActive ? C.green : C.text, fontSize: 14, fontWeight: isActive ? 800 : 600,
                                                    borderTop: `1px solid ${C.border}`,
                                                }}
                                            >
                                                {tab.label}
                                                {isActive && (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div style={{
                        display: 'flex', gap: 0, borderBottom: '2px solid ' + C.border,
                        marginBottom: 24, overflowX: 'auto',
                    }}>
                        {allTabs.map((tab) => {
                            const isActive = activeSection === tab.key;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveSection(tab.key)}
                                    style={{
                                        padding: '10px 18px', border: 'none', cursor: 'pointer',
                                        background: 'transparent', fontFamily: FONT, fontSize: 13, fontWeight: 600,
                                        color: isActive ? C.green : C.muted,
                                        borderBottom: `2px solid ${isActive ? C.green : 'transparent'}`,
                                        marginBottom: -2, whiteSpace: 'nowrap',
                                        transition: 'color 0.15s, border-color 0.15s',
                                    }}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Content */}
                {activeSection === 'overview' && (
                    <div ref={overviewRef}>
                        <OverviewSection data={summaryData} loading={loading} currency={currency} t={t} />
                    </div>
                )}
                {activeSection === 'financial' && <FinancialPerformancePage parentDates={dateRange} />}
                {activeSection === 'products' && <ProductIntelligencePage parentDates={dateRange} />}
                {activeSection === 'costs' && <CostManagementPage parentDates={dateRange} />}
                {activeSection === 'operations' && <OperationalEfficiencyPage parentDates={dateRange} />}

                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
        </PortalLayout>
    );
};

export default PortalReportingPage;
